<template>
  <div class="relative h-full">
    <div
      v-if="offering.is_most_popular"
      class="absolute -top-3 left-1/2 z-10 flex h-[25px] -translate-x-1/2 items-center justify-center gap-0.5 whitespace-nowrap rounded-full bg-[linear-gradient(98deg,#f1c26d_11%,#a47d39_67%)] px-4 py-1 shadow-md"
    >
      <Icon
        name="Star"
        :size="14"
        class="fill-white text-white dark:text-lightest dark:fill-lightest"
      />
      <span
        class="font-inter text-xs font-bold uppercase tracking-wide text-white dark:text-lightest"
      >
        Most Popular
      </span>
    </div>

    <div
      class="relative flex h-full flex-col overflow-hidden rounded-3xl border bg-white border-[#dfc695] dark:bg-primary-7 dark:border-[#516679]"
    >
      <img
        :src="rippleSrc"
        alt=""
        class="pointer-events-none absolute -top-4 -right-4 z-0 select-none h-[194px] w-[215px]"
        :class="!offering.is_most_popular ? 'opacity-30' : 'opacity-50'"
      />

      <div class="relative z-10 flex flex-1 flex-col py-6 px-4">
        <!-- HEADER -->
        <div class="flex items-start justify-between">
          <div class="flex flex-col gap-[7px]">
            <span
              class="w-fit rounded-lg bg-paper-3 px-2 py-1 text-xs font-bold font-inter uppercase text-paper-8 dark:text-secondary-5 dark:bg-[rgba(218,170,0,0.2)]"
            >
              {{ offering.title }}
            </span>
            <div class="font-inter text-base font-semibold text-strongest">
              {{ offering.sub_title
              }}<sup v-if="offering.sub_title_footnote_ref" class="ml-0.5 text-[#bfbfbf]">{{
                offering.sub_title_footnote_ref
              }}</sup>
            </div>
          </div>
          <div
            class="flex flex-col text-strongest items-end rounded-lg p-2 bg-white dark:bg-primary-7 font-inter text-base font-medium"
          >
            <span class="">${{ commaSeparateThousands(offering.minimum_investment) }}</span>
            <span class="">Minimum</span>
          </div>
        </div>

        <!-- RATE -->
        <div class="mt-6">
          <div
            class="font-league text-5xl leading-[0.8] tracking-[-0.96] text-[#141414] dark:text-white"
            :class="offering.is_most_popular ? 'font-semibold' : 'font-medium'"
          >
            {{ offering.rate_range }}
          </div>
          <div class="font-inter text-sm font-medium text-[#8c8c8c]">Annual interest rate</div>
        </div>

        <!-- OPEN TO -->
        <div class="my-6 flex items-center gap-2 text-sm font-medium text-strongest">
          <Icon :name="offering.icon_name" :size="16" class="shrink-0 text-paper-7" />
          {{ offering.open_to == "all" ? "Open to all investors" : "Open to accredited investors" }}
        </div>

        <!-- INVEST CTA -->
        <Button
          @click="handleMakeInvestmentClick(offering)"
          label
          size="md"
          variant="primary-secondary-neo"
          class="w-full"
        >
          <template #label>
            <span class="text-lg font-inter">
              Start with ${{ commaSeparateThousands(offering.minimum_investment) }}</span
            >
          </template>
        </Button>

        <div class="my-[23.5px] border-t border-outline-dividers"></div>

        <!-- RATE BY TERM/INTERVAL HEADER-->
        <div class="flex items-center justify-between">
          <div class="font-inter text-xs font-bold text-strong dark:text-[#9e7400] uppercase">
            {{ offering.is_short_term ? "Rate by Interval" : "Rate by Term"
            }}<sup v-if="offering.is_short_term" class="ml-0.5 text-[#bfbfbf]">2</sup>
          </div>
          <span
            v-if="offering.has_rate_lift"
            class="w-fit rounded-lg bg-paper-3 px-2 py-1 text-[10px] font-bold font-inter uppercase text-paper-8 dark:text-secondary-5 dark:bg-[rgba(218,170,0,0.2)]"
          >
            Rate Lift Applied
          </span>
        </div>

        <!-- RATE BY TERM/INTERVAL BODY -->
        <div class="mt-3 grid grid-cols-[max-content_1fr_max-content] items-center gap-2">
          <div v-for="(item, index) in offering.rate_by_term" :key="item.term" class="contents">
            <span
              class="font-inter text-base"
              :class="
                index === 0
                  ? ' font-semibold  text-[#75613f] dark:text-secondary-5'
                  : 'font-medium text-strongest'
              "
            >
              {{ item.rate }}%
              <span v-if="index === 0" class="ml-0.5 text-sm font-bold uppercase">Best</span>
            </span>
            <span class="border-b border-outline-dividers"></span>
            <span
              class="font-inter text-sm"
              :class="
                index === 0
                  ? 'font-bold  text-[#75613f] dark:text-secondary-5'
                  : ' text-strong font-medium'
              "
            >
              {{ offering.is_short_term ? `${item.term} months` : `${item.term / 12} years` }}
            </span>
          </div>
          <div
            v-for="n in maxRateByTermLength - offering.rate_by_term.length"
            :key="`filler-${n}`"
            class="invisible contents"
          >
            <span class="font-inter text-base">&nbsp;</span>
            <span class="border-b border-outline-dividers"></span>
            <span class="font-inter text-sm">&nbsp;</span>
          </div>
        </div>

        <!-- OFFERING DETAILS -->
        <div class="mt-6 font-inter text-xs font-bold text-paper-8 dark:text-secondary-7 uppercase">
          Offering Details
        </div>
        <div class="mt-2 flex flex-col gap-2">
          <div v-for="item in offering.details" :key="item.text" class="flex gap-2">
            <Icon name="Check" :size="16" :stroke-width="3" class="mt-0.5 text-paper-7" />
            <span class="font-inter text-sm font-semibold text-left text-strong">
              {{ item.text
              }}<sup v-if="item.footnote_ref" class="ml-0.5 text-xs text-light">{{
                item.footnote_ref
              }}</sup>
            </span>
          </div>
          <div
            v-for="n in maxDetailsLength - offering.details.length"
            :key="`details-filler-${n}`"
            class="invisible flex gap-2"
          >
            <Icon name="Check" :size="16" :stroke-width="3" class="mt-0.5" />
            <span class="font-inter text-sm font-semibold">&nbsp;</span>
          </div>
        </div>

        <!-- PROSPECTUS/PRIVATE PLACEMENT MEMO -->
        <div v-if="offering.link" class="mt-auto pt-6 flex items-center gap-2">
          <Icon name="FileText" :size="16" class="shrink-0 text-paper-8 dark:text-paper-2" />
          <a
            :href="offering.link"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm font-bold text-paper-8 underline decoration-paper-8 underline-offset-2 dark:text-paper-2 dark:decoration-paper-2"
          >
            {{ offering.link_title }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { Button, Icon } from "@/components";
import { commaSeparateThousands } from "@/utilities";
import { useAnalytics } from "@/composables/useAnalytics";
import AsteriskNotice from "@/components/AsteriskNotice.vue";
import StepDescriptionModal from "./PurchaseFlowUtilities/StepDescriptionModal.vue";
import S1DisclaimerModal from "@/components/S1DisclaimerModal.vue";
import { usePurchaseFlowStore, useDataStore, useCrudStore, useUIStore } from "@/stores";
const router = useRouter();
import ripple_lightmode from "@/assets/images/ripple_lightmode.png";
import ripple_darkmode from "@/assets/images/ripple_darkmode.png";

const purchaseFlowStore = usePurchaseFlowStore();
const dataStore = useDataStore();
const UIStore = useUIStore();

const { trackOfferingCtaClicked } = useAnalytics();

const isStepDescriptionModalOpen = ref(false);

const props = defineProps({
  offering: {
    type: Object,
    default: {},
  },
  maxRateByTermLength: {
    type: Number,
    default: 0,
  },
  maxDetailsLength: {
    type: Number,
    default: 0,
  },
});

const rippleSrc = computed(() => {
  return UIStore.is_dark_mode ? ripple_darkmode : ripple_lightmode;
});

const handleMakeInvestmentClick = async (offering) => {
  trackOfferingCtaClicked(
    `Start with $${commaSeparateThousands(offering.minimum_investment)}`,
    offering.minimum_investment,
    offering.type
  );

  const matchingOfferings = dataStore.offerings.filter((off) =>
    offering.type === "Reg D" && !offering.is_short_term
      ? off.type === offering.type &&
        off.tier === purchaseFlowStore.regDTier &&
        off.is_short_term === offering.is_short_term
      : off.type === offering.type && off.is_short_term === offering.is_short_term
  );

  const selectedOffering = matchingOfferings.sort((a, b) => b.interest_rate - a.interest_rate)[0];

  purchaseFlowStore.selected_offering = selectedOffering;

  // 2. Set short-term flags
  if (offering.is_short_term) {
    purchaseFlowStore.current_investment.is_compounded = true;
    purchaseFlowStore.current_investment.is_short_term = true;
  } else {
    purchaseFlowStore.current_investment.is_short_term = false;
  }

  // 3. Default the amount based on offering type
  const defaultAmount =
    offering.type === "Reg D" ? 25000 : selectedOffering.is_short_term ? 1000 : 5000;
  const currentAmount = purchaseFlowStore.current_investment.amount || 0;
  const previousOfferingType = purchaseFlowStore.current_investment.offering_type;
  const isReturningToSameType = previousOfferingType === offering.type;

  // Reinvestors arrive here with the amount already set to what's available to roll
  // over — never stomp it with the offering's default starting amount.
  const shouldResetAmount =
    !purchaseFlowStore.selected_rollover &&
    (!isReturningToSameType || currentAmount <= defaultAmount);

  if (shouldResetAmount) {
    purchaseFlowStore.current_investment.amount = defaultAmount;
  }

  // 4. Navigate
  purchaseFlowStore.current_step = 1;
  router.push("/make-an-investment");
  await purchaseFlowStore.saveInvestmentProgress();
};
</script>
